// src/components/workout/WorkoutProgramPreview.jsx
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dumbbell, Clock, Calendar, Activity, Target, Users } from "lucide-react";

const WorkoutProgramPreview = ({ 
  open, 
  onOpenChange, 
  programData,
  unitSystem = 'imperial',
  onActivate
}) => {
  if (!programData) return null;
  
  const { name, description, goal, level, frequency, workouts } = programData;
  const workoutDays = Object.keys(workouts || {});
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle className="text-2xl">{name}</DialogTitle>
              <DialogDescription className="mt-1">
                {description}
              </DialogDescription>
            </div>
            {onActivate && (
              <Button 
                onClick={() => {
                  onActivate();
                  onOpenChange(false);
                }}
              >
                Set As Active Program
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="flex flex-wrap gap-2 my-4">
          <Badge variant="outline" className="flex items-center gap-1">
            <Target className="h-3.5 w-3.5" />
            <span>{goal?.charAt(0).toUpperCase() + goal?.slice(1) || 'General'}</span>
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            <span>{level?.charAt(0).toUpperCase() + level?.slice(1) || 'All Levels'}</span>
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>{frequency || 'Flexible'}</span>
          </Badge>
        </div>
        
        {workoutDays.length > 0 ? (
          <Tabs defaultValue={workoutDays[0]} className="flex-grow flex flex-col">
            <TabsList className="grid" style={{ gridTemplateColumns: `repeat(${Math.min(workoutDays.length, 5)}, 1fr)` }}>
              {workoutDays.map(day => (
                <TabsTrigger key={day} value={day}>
                  {workouts[day]?.name || `Day ${day}`}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {workoutDays.map(day => (
              <TabsContent key={day} value={day} className="flex-grow">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>{workouts[day]?.name || `Day ${day}`}</CardTitle>
                    <CardDescription>
                      {workouts[day]?.exercises?.length || 0} exercises
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {workouts[day]?.exercises?.map((exercise, index) => (
                          <div key={index} className="p-4 border rounded-lg space-y-2">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center gap-2">
                                <Dumbbell className="h-5 w-5 text-primary" />
                                <h3 className="font-semibold text-base">{exercise.name}</h3>
                              </div>
                              <Badge variant="outline">
                                {exercise.type?.charAt(0).toUpperCase() + exercise.type?.slice(1) || 'Other'}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Sets:</span>
                                <span className="font-medium">{exercise.sets || 3}</span>
                              </div>
                              
                              {exercise.repType === 'static' ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-muted-foreground">Reps:</span>
                                  <span className="font-medium">{exercise.reps || 8}</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <span className="text-muted-foreground">Reps:</span>
                                  <span className="font-medium">{exercise.repMin || 8}-{exercise.repMax || 12}</span>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Rest:</span>
                                <span className="font-medium">{exercise.restTime || 90}s</span>
                              </div>
                              
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Increment:</span>
                                <span className="font-medium">{exercise.increment || 5}{unitSystem === 'imperial' ? 'lbs' : 'kg'}</span>
                              </div>
                              
                              {exercise.optional && (
                                <div className="col-span-2 md:col-span-1">
                                  <Badge variant="secondary">Optional</Badge>
                                </div>
                              )}
                            </div>
                            
                            {exercise.notes && (
                              <div className="text-sm mt-2 text-muted-foreground">
                                <p className="italic">{exercise.notes}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <div className="flex-grow flex items-center justify-center">
            <p className="text-muted-foreground">No workout days defined in this program.</p>
          </div>
        )}
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkoutProgramPreview;